import { AdObject, AdType, AdsProvider, setupAdPluginForPlayer } from './ads';
import { AnalyticsOptions, setupAnalyticsForPlayer } from './analytics';
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';

import { EventWithDocId } from '@yoimo/client-sdk/events';
import { VideoJsPlayer } from 'video.js';
import { VideoWithDocId } from '@yoimo/client-sdk/videos';
import { ensureVideojsIsInitialized } from './ensure-videojs-is-initialized';
import { getPreroll } from './hardcoded-prerolls';
import { getVideoJSConfig } from './get-video-js-config';
import { logger } from '@core/services';
import { setupChromecastForPlayer } from './chromecast';
import videojs from 'video.js';

@Component({
  // This selector forces the input video to be provided
  selector: 'joymo-video-player',
  templateUrl: './video-player.component.html',
  styleUrls: ['./video-player.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class VideoPlayerComponent implements OnInit, OnDestroy {
  @Input() video?: VideoWithDocId;
  @Input() event?: EventWithDocId;
  @Input() analyticsContextData?: Partial<AnalyticsOptions>;
  @Output() fullScreenChange = new EventEmitter<boolean>();
  @Output() videoStarted = new EventEmitter<Date>();
  @Output() videoPaused = new EventEmitter<Date>();
  @Output() videoStopped = new EventEmitter<Date>();

  readonly outputEvents = {
    play: () => {
      this.videoStarted.emit(new Date());
    },
    pause: () => {
      this.videoPaused.emit(new Date());
    },
    ended: () => {
      this.videoStopped.emit(new Date());
    },
    fullscreenchange: () => {
      this.fullScreenChange.emit(this.player?.isFullscreen());
    },
  };

  @ViewChild('player', { static: true }) playerElement?: ElementRef;

  private player?: VideoJsPlayer;

  constructor() {
    ensureVideojsIsInitialized(videojs);
  }

  get _video(): VideoWithDocId {
    if (!this.video) {
      throw new TypeError('no video input provided to the component');
    }
    return this.video;
  }

  get _player(): VideoJsPlayer {
    if (!this.player) {
      throw new TypeError(
        'Your are trying to access a player before it was initialized.'
      );
    }
    return this.player;
  }

  ngOnInit(): void {
    if (!this.playerElement) {
      logger.logCriticalError(
        'Failed to get the video player',
        'CONTENT-ACCESS'
      );
      throw new TypeError('The player element was not found');
    }
    if (!this.video) {
      throw new TypeError('Video was not provided');
    }
    this.player = videojs(
      this.playerElement.nativeElement,
      {
        ...getVideoJSConfig(
          !videojs.browser.IS_ANY_SAFARI && !videojs.browser.IS_IOS
        ),
        poster: this._video.posterImage,
        sources: [{ src: this._video.videoLocation }],
      },
      function onPlayerReady() {}
    );
    this.setUpEvents(this.player);
    this.setupAnalytics(this.player, this.video, this.event);
    this.setupAdsPlugin(this.player, this.video);
    this.setupChromecast(this.player);
  }

  setupChromecast(player: VideoJsPlayer) {
    setupChromecastForPlayer(player);
  }

  ngOnDestroy(): void {
    if (this.player) {
      this.clearEvents(this.player);
      this.player.dispose();
    }
  }

  setUpEvents(player: VideoJsPlayer) {
    for (const [e, callbackFn] of Object.entries(this.outputEvents)) {
      player.on(e, callbackFn);
    }
  }

  clearEvents(player: VideoJsPlayer) {
    for (const [e, callbackFn] of Object.entries(this.outputEvents)) {
      player.off(e, callbackFn);
    }
  }

  setupAnalytics(
    player: VideoJsPlayer,
    video: VideoWithDocId,
    event?: EventWithDocId
  ) {
    // #TODO: add more context from the video to the metadata
    setupAnalyticsForPlayer(player, {
      ...(this.analyticsContextData || {}),
      'content.title': video.name,
      'content.channel': video.channelId,
      'content.tvShow': event ? event.docId : 'CHANNEL_WIDE',
      'content.season': event ? event.title : 'CHANNEL_WIDE',
      'content.duration': video.duration || undefined,
      'content.isLive': video.streamState === 'LIVE',
      'content.id': video.docId,
      'content.streamingProtocol': 'HLS',
      'content.metadata': {
        tags: video.tags.sort().join('|'),
        attr0: video.attr0 || '',
        attr1: video.attr1 || '',
        attr2: video.attr2 || '',
        attr3: video.attr3 || '',
      },
      'content.type': video.type,
      'content.imdbId':
        (video.startTime || video.uploadTime)?.toISOString().split('T')[0] ||
        '',
    });
  }
  setupAdsPlugin(player: VideoJsPlayer, video: VideoWithDocId) {
    if (video.type === 'SHORT') {
      return;
    }
    /** EventID: Preroll Url*/
    const prerollUrl = getPreroll(video);
    if (!prerollUrl) {
      return;
    }
    const preroll: AdObject = {
      name: 'Preroll',
      allowSkip: false,
      adSign: false,
      videos: [
        {
          fileName: prerollUrl.url,
          href: prerollUrl.href,
          src: prerollUrl.url,
        },
      ],
      showRemaining: false,
      target: undefined,
      skipTime: 10,
      type: 'VIDEO',
    };

    setupAdPluginForPlayer(player, {
      adsProvider: {
        cuePoints: () => Promise.resolve([{ time: 'START', id: 'preroll' }]),
        getAd: async (_t: AdType, _cuePointId: string, _isLive: boolean) => {
          if (_t !== 'PREROLL') {
            return undefined;
          }
          return preroll;
        },
        getExpectedLiveStreamLength: () => {
          return video.streamState === 'LIVE' &&
            video.endTime &&
            video.startTime
            ? (video.endTime.getTime() - video.startTime.getTime()) / 1000
            : video.duration || 0;
        },
      },
    });
  }
}
